import { useCallback } from "react";

import { Action, RunWithPosition } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";

import useHasEntityWriteAccess from "./useHasEntityWriteAccess";

const SET_RUN_PRIORITY_ACCESS_TYPES = [Action.RunPrioritizeSet];

const hasPrioritizeRunAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(SET_RUN_PRIORITY_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can set a run priority.
 * A user needs either write access to the entity or the RunSetPriority permission to set a run priority.
 * @returns {(entity?: Pick<RunWithPosition, "space"> & { isRunFinished: boolean; hasWorkerPool: boolean; canWrite?: boolean }) => AccessResult} Function that takes an optional run and entity and returns a AccessResult indicating if user can replan the run
 */
const useCanPrioritizeRun = (): ((
  entity?: Pick<RunWithPosition, "space"> & {
    isRunFinished: boolean;
    hasWorkerPool: boolean;
    canWrite?: boolean;
    isPublicWorkerPool?: boolean;
  }
) => AccessResult) => {
  const hasEntityWriteAccess = useHasEntityWriteAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (entity) => {
      if (!entity) {
        return { result: false };
      }

      if (entity.isRunFinished || !entity.hasWorkerPool || entity.isPublicWorkerPool) {
        return { result: false };
      }

      const hasAccessToReview =
        hasEntityWriteAccess({ spaceDetails: entity.space, canWrite: entity.canWrite }) ||
        hasPrioritizeRunAccess(viewer, entity?.space.id);

      if (hasAccessToReview) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasEntityWriteAccess, viewer]
  );
};

export default useCanPrioritizeRun;
